import { proxy, subscribe } from 'valtio/vanilla'

/**
 * Available accent colors for the theme
 */
export const ACCENT_COLORS = {
  violet: { name: 'Violet', value: '#8b5cf6' },
  pink: { name: 'Pink', value: '#ec4899' },
  cyan: { name: 'Cyan', value: '#06b6d4' },
  green: { name: 'Green', value: '#22c55e' },
  orange: { name: 'Orange', value: '#f97316' },
  red: { name: 'Red', value: '#ef4444' },
  blue: { name: 'Blue', value: '#3b82f6' },
  yellow: { name: 'Yellow', value: '#eab308' },
  lime: { name: 'Lime', value: '#84cc16' },
  teal: { name: 'Teal', value: '#14b8a6' },
  indigo: { name: 'Indigo', value: '#6366f1' },
  fuchsia: { name: 'Fuchsia', value: '#d946ef' },
  rose: { name: 'Rose', value: '#f43f5e' },
  amber: { name: 'Amber', value: '#f59e0b' },
  emerald: { name: 'Emerald', value: '#10b981' },
  sky: { name: 'Sky', value: '#0ea5e9' },
} as const

export type AccentColorKey = keyof typeof ACCENT_COLORS
export type ThemeMode = 'dark' | 'light'

const STORAGE_KEY = 'my-arcade-theme'

/**
 * Theme state interface
 */
export interface ThemeState {
  /** Current accent color key */
  accentColor: AccentColorKey
  /** Current theme mode (dark/light) */
  mode: ThemeMode
}

/**
 * Load theme from local storage
 */
function loadThemeFromStorage(): ThemeState {
  const defaultTheme: ThemeState = {
    accentColor: 'violet',
    mode: 'dark',
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<ThemeState>
      return {
        accentColor: isValidAccentColor(parsed.accentColor)
          ? parsed.accentColor
          : defaultTheme.accentColor,
        mode: isValidMode(parsed.mode) ? parsed.mode : defaultTheme.mode,
      }
    }
  } catch {
    // Ignore storage errors
  }

  return defaultTheme
}

/**
 * Validate accent color key
 */
function isValidAccentColor(color: unknown): color is AccentColorKey {
  return typeof color === 'string' && color in ACCENT_COLORS
}

/**
 * Validate theme mode
 */
function isValidMode(mode: unknown): mode is ThemeMode {
  return mode === 'dark' || mode === 'light'
}

/**
 * Save theme to local storage
 */
function saveThemeToStorage(state: ThemeState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Ignore storage errors
  }
}

/**
 * The main theme store
 */
export const themeStore = proxy<ThemeState>(loadThemeFromStorage())

// Subscribe to changes and persist to local storage
subscribe(themeStore, () => {
  saveThemeToStorage({
    accentColor: themeStore.accentColor,
    mode: themeStore.mode,
  })
  applyTheme()
})

/**
 * Set the accent color
 */
export function setAccentColor(color: AccentColorKey): void {
  if (isValidAccentColor(color)) {
    themeStore.accentColor = color
  }
}

/**
 * Set the theme mode
 */
export function setThemeMode(mode: ThemeMode): void {
  if (isValidMode(mode)) {
    themeStore.mode = mode
  }
}

/**
 * Toggle between dark and light mode
 */
export function toggleThemeMode(): void {
  themeStore.mode = themeStore.mode === 'dark' ? 'light' : 'dark'
}

/**
 * Get the current accent color value
 */
export function getAccentColorValue(): string {
  return ACCENT_COLORS[themeStore.accentColor].value
}

/**
 * Apply the current theme to the document
 */
export function applyTheme(): void {
  const root = document.documentElement
  const accentValue = ACCENT_COLORS[themeStore.accentColor].value

  // Set CSS custom property for accent color
  root.style.setProperty('--accent-color', accentValue)

  // Apply dark/light mode class
  if (themeStore.mode === 'dark') {
    root.classList.add('dark')
    root.classList.remove('light')
  } else {
    root.classList.add('light')
    root.classList.remove('dark')
  }
}

/**
 * Initialize theme on page load
 */
export function initTheme(): void {
  applyTheme()
}

/**
 * Reset theme to default (for testing)
 */
export function resetTheme(): void {
  themeStore.accentColor = 'violet'
  themeStore.mode = 'dark'
}
